(() => {
    "use strict";

    const USE_HOME_READER_TRANSITION = true;

    const writing = document.querySelector(
        "[data-home-minimal-featured-writing]"
    );

    if (!writing) {
        return;
    }

    const catalog =
        window.frBooksCatalog;

    if (!catalog) {
        console.error(
            "frBooksCatalog must load before zcode-home-minimal-featured.js."
        );

        return;
    }

    const items = Array.from(
        writing.querySelectorAll(
            "[data-home-minimal-featured-writing-item]"
        )
    ).filter(
        (item) => {
            const slug =
                item.dataset.postSlug ||
                "";

            return Boolean(
                catalog.getChapter(
                    slug
                )
            );
        }
    );

    if (!items.length) {
        return;
    }

    const PASSAGE_WORDS =
        125;

    const WORDS_PER_PAGE =
        250;

    const MAX_EXCERPTS =
        5;

    /*
     * The final portion of a work is never sampled.
     */
    const HIDDEN_END_RATIO =
        0.08;

    let currentIndex =
        0;


    // ==========================================================
    // HELPERS
    // ==========================================================

    function normalize(
        value
    ) {
        return String(
            value ||
            ""
        )
            .toLowerCase()
            .replace(
                /^#?book:\s*/i,
                ""
            )
            .replace(
                /^hash-book-/i,
                ""
            )
            .replace(
                /^book-/i,
                ""
            )
            .replace(
                /&/g,
                "and"
            )
            .replace(
                /[^a-z0-9]+/g,
                "-"
            )
            .replace(
                /^-+|-+$/g,
                ""
            );
    }


    function getTags(
        item
    ) {
        return Array.from(
            item.querySelectorAll(
                "[data-home-minimal-featured-tags] [data-tag-name]"
            )
        ).map(
            (tag) => ({
                name:
                    tag.dataset.tagName ||
                    "",

                slug:
                    tag.dataset.tagSlug ||
                    ""
            })
        );
    }


    function getBookTag(
        tags
    ) {
        return (
            tags.find(
                (tag) =>
                    /^#?book:/i.test(
                        tag.name
                    )
            ) ||
            null
        );
    }


    function getBookDisplayName(
        tag
    ) {
        if (
            !tag
        ) {
            return "";
        }

        return tag.name
            .replace(
                /^#?book:\s*/i,
                ""
            )
            .trim();
    }


    function getTypeFromTags(
        tags
    ) {
        for (
            const tag
            of tags
            ) {
            const value =
                `${tag.name} ${tag.slug}`
                    .toLowerCase();

            if (
                value.includes(
                    "04_fiction"
                ) ||
                value.includes(
                    "04-fiction"
                ) ||
                /\bfiction\b/.test(
                    value
                )
            ) {
                return "Fiction";
            }

            if (
                value.includes(
                    "04_nonfiction"
                ) ||
                value.includes(
                    "04-nonfiction"
                ) ||
                value.includes(
                    "non-fiction"
                ) ||
                value.includes(
                    "nonfiction"
                )
            ) {
                return "Non-Fiction";
            }

            if (
                value.includes(
                    "04_memoir"
                ) ||
                value.includes(
                    "04-memoir"
                ) ||
                value.includes(
                    "memoir"
                )
            ) {
                return "Memoir";
            }
        }

        return "";
    }


    function normalizeType(
        value
    ) {
        const clean =
            String(
                value ||
                ""
            )
                .trim()
                .toLowerCase();

        if (
            clean ===
            "fiction"
        ) {
            return "Fiction";
        }

        if (
            clean ===
            "nonfiction" ||
            clean ===
            "non-fiction" ||
            clean ===
            "non fiction"
        ) {
            return "Non-Fiction";
        }

        if (
            clean ===
            "memoir"
        ) {
            return "Memoir";
        }

        return "";
    }


    // ==========================================================
    // VIEWER-SPECIFIC BOOK METADATA
    //
    // The former version fetched data-books.json, which exposed
    // the complete master book catalog to every browser.
    //
    // We now resolve the Book through frBooksCatalog. That
    // catalog contains only books the current viewer is allowed
    // to know about.
    // ==========================================================

    function findCatalogBook(
        bookTag
    ) {
        if (
            !bookTag
        ) {
            return null;
        }

        if (
            bookTag.slug
        ) {
            const directMatch =
                catalog.getBook(
                    bookTag.slug
                );

            if (
                directMatch
            ) {
                return directMatch;
            }
        }

        const slugKey =
            normalize(
                bookTag.slug
            );

        const nameKey =
            normalize(
                bookTag.name
            );

        return (
            catalog
                .getBooks()
                .find(
                    (bookRecord) => {
                        const recordSlugKey =
                            normalize(
                                bookRecord?.slug
                            );

                        const recordTitleKey =
                            normalize(
                                bookRecord?.title
                            );

                        const recordTagNameKey =
                            normalize(
                                bookRecord?.tagName
                            );

                        return (
                            recordSlugKey ===
                            slugKey ||
                            recordSlugKey ===
                            nameKey ||
                            recordTitleKey ===
                            slugKey ||
                            recordTitleKey ===
                            nameKey ||
                            recordTagNameKey ===
                            slugKey ||
                            recordTagNameKey ===
                            nameKey
                        );
                    }
                ) ||
            null
        );
    }


    // ==========================================================
    // READ URL
    // ==========================================================

    function getReadUrl(
        item
    ) {
        const tags =
            getTags(
                item
            );

        const bookTag =
            getBookTag(
                tags
            );

        const catalogBook =
            findCatalogBook(
                bookTag
            );

        const chapterSlug =
            item.dataset.postSlug ||
            "";

        if (
            !catalogBook?.slug ||
            !chapterSlug
        ) {
            return "";
        }

        return (
            `/books/?book=${encodeURIComponent(
                catalogBook.slug
            )}` +
            `&chapter=${encodeURIComponent(
                chapterSlug
            )}`
        );
    }


    // ==========================================================
    // SOURCE CONTENT
    // ==========================================================

    function getSourceParagraphs(
        item
    ) {
        const source =
            item.querySelector(
                "[data-home-minimal-featured-full-content]"
            );

        if (
            !source
        ) {
            return [];
        }

        const paragraphs =
            Array.from(
                source.querySelectorAll(
                    "p"
                )
            )
                .map(
                    (paragraph) =>
                        paragraph.textContent
                            .replace(
                                /\s+/g,
                                " "
                            )
                            .trim()
                )
                .filter(
                    Boolean
                );

        /*
         * Apply the same cleanup used for the normal opening
         * preview.
         */
        if (
            paragraphs.length
        ) {
            paragraphs[0] =
                paragraphs[0].replace(
                    /^\s*\[\s*\]\s*.*?[—–-]\s*/i,
                    ""
                );
        }

        return paragraphs.filter(
            Boolean
        );
    }


    function getStructuredWords(
        item
    ) {
        const words =
            [];

        getSourceParagraphs(
            item
        ).forEach(
            (
                paragraphText,
                paragraphIndex
            ) => {
                paragraphText
                    .split(
                        /\s+/
                    )
                    .filter(
                        Boolean
                    )
                    .forEach(
                        (word) => {
                            words.push({
                                word,
                                paragraphIndex
                            });
                        }
                    );
            }
        );

        return words;
    }


    // ==========================================================
    // PAGE COUNT
    // ==========================================================

    function updatePageCount(
        item
    ) {
        const pageCount =
            item.querySelector(
                "[data-home-minimal-featured-page-count]"
            );

        if (
            !pageCount
        ) {
            return;
        }

        const totalWords =
            getStructuredWords(
                item
            ).length;

        if (
            !totalWords
        ) {
            pageCount.textContent =
                "";

            return;
        }

        const pages =
            Math.max(
                1,
                Math.ceil(
                    totalWords /
                    WORDS_PER_PAGE
                )
            );

        pageCount.textContent =
            pages ===
            1
                ? "1 PAGE"
                : `${pages} PAGES`;
    }


    // ==========================================================
    // CONTINUE READING
    // ==========================================================

    function appendContinueReading(
        paragraph,
        readUrl
    ) {
        if (
            !paragraph ||
            !readUrl
        ) {
            return;
        }

        paragraph.appendChild(
            document.createTextNode(
                " "
            )
        );

        const continueLink =
            document.createElement(
                "a"
            );

        continueLink.className =
            "fr-home-minimal-featured-writing__continueReading";

        continueLink.href =
            readUrl;

        continueLink.textContent =
            "(Read entire piece)";

        paragraph.appendChild(
            continueLink
        );
    }


    // ==========================================================
    // PASSAGE WINDOWS
    // ==========================================================

    function getExcerptWindows(
        item
    ) {
        const totalWords =
            getStructuredWords(
                item
            ).length;

        if (
            !totalWords
        ) {
            return [];
        }

        const hiddenTail =
            Math.max(
                1,
                Math.floor(
                    totalWords *
                    HIDDEN_END_RATIO
                )
            );

        const usableWords =
            Math.max(
                1,
                totalWords -
                hiddenTail
            );

        /*
         * A normal excerpt is 125 words. Very short works
         * receive one excerpt rather than manufacturing several
         * heavily overlapping excerpts.
         */
        const excerptWords =
            Math.min(
                PASSAGE_WORDS,
                usableWords
            );

        let excerptCount =
            Math.floor(
                usableWords /
                excerptWords
            );

        excerptCount =
            Math.max(
                1,
                Math.min(
                    MAX_EXCERPTS,
                    excerptCount
                )
            );

        if (
            excerptCount ===
            1
        ) {
            return [
                {
                    start:
                        0,

                    words:
                    excerptWords
                }
            ];
        }

        const maxStart =
            usableWords -
            excerptWords;

        const windows =
            [];

        for (
            let index = 0;
            index <
            excerptCount;
            index += 1
        ) {
            const start =
                Math.round(
                    (
                        maxStart *
                        index
                    ) /
                    (
                        excerptCount -
                        1
                    )
                );

            windows.push({
                start,
                words:
                excerptWords
            });
        }

        /*
         * Safety check: remove any window that overlaps the
         * preceding one.
         */
        return windows.filter(
            (
                excerptWindow,
                index,
                list
            ) => {
                if (
                    index ===
                    0
                ) {
                    return true;
                }

                const previous =
                    list[
                    index -
                    1
                        ];

                return (
                    excerptWindow.start >=
                    previous.start +
                    previous.words
                );
            }
        );
    }


    // ==========================================================
    // EXCERPT CIRCLES
    // ==========================================================

    function buildExcerptControls(
        item
    ) {
        const container =
            item.querySelector(
                "[data-home-minimal-featured-excerpts]"
            );

        if (
            !container
        ) {
            return;
        }

        const windows =
            getExcerptWindows(
                item
            );

        item._featuredExcerptWindows =
            windows;

        item._featuredExcerptIndex =
            0;

        container.innerHTML =
            "";

        windows.forEach(
            (
                excerptWindow,
                index
            ) => {
                const button =
                    document.createElement(
                        "button"
                    );

                button.type =
                    "button";

                button.className =
                    "fr-home-minimal-featured-excerpts__button";

                button.dataset.homeMinimalFeaturedExcerptIndex =
                    String(
                        index
                    );

                button.setAttribute(
                    "aria-label",
                    `Excerpt ${index + 1}`
                );

                button.setAttribute(
                    "aria-pressed",
                    index ===
                    0
                        ? "true"
                        : "false"
                );

                if (
                    index ===
                    0
                ) {
                    button.classList.add(
                        "is-active"
                    );
                }

                container.appendChild(
                    button
                );
            }
        );
    }


    function updateExcerptControls(
        item,
        selectedIndex
    ) {
        const buttons =
            Array.from(
                item.querySelectorAll(
                    "[data-home-minimal-featured-excerpt-index]"
                )
            );

        buttons.forEach(
            (
                button,
                index
            ) => {
                const active =
                    index ===
                    selectedIndex;

                button.classList.toggle(
                    "is-active",
                    active
                );

                button.setAttribute(
                    "aria-pressed",
                    active
                        ? "true"
                        : "false"
                );
            }
        );

        item._featuredExcerptIndex =
            selectedIndex;
    }


    // ==========================================================
    // RENDER AN EXCERPT
    // ==========================================================

    function showExcerpt(
        item,
        excerptIndex
    ) {
        if (
            !item
        ) {
            return;
        }

        const windows =
            item._featuredExcerptWindows ||
            getExcerptWindows(
                item
            );

        const excerptWindow =
            windows[
                excerptIndex
                ];

        if (
            !excerptWindow
        ) {
            return;
        }

        const body =
            item.querySelector(
                ".fr-home-minimal-featured-writing__body"
            );

        if (
            !body
        ) {
            return;
        }

        const words =
            getStructuredWords(
                item
            );

        const selectedWords =
            words.slice(
                excerptWindow.start,
                excerptWindow.start +
                excerptWindow.words
            );

        if (
            !selectedWords.length
        ) {
            return;
        }

        const passageParagraphs =
            [];

        selectedWords.forEach(
            ({
                 word,
                 paragraphIndex
             }) => {
                let current =
                    passageParagraphs[
                    passageParagraphs.length -
                    1
                        ];

                if (
                    !current ||
                    current.paragraphIndex !==
                    paragraphIndex
                ) {
                    current = {
                        paragraphIndex,
                        words: []
                    };

                    passageParagraphs.push(
                        current
                    );
                }

                current.words.push(
                    word
                );
            }
        );

        body.innerHTML =
            "";

        passageParagraphs.forEach(
            (
                passageParagraph,
                index
            ) => {
                const paragraph =
                    document.createElement(
                        "p"
                    );

                let text =
                    passageParagraph.words.join(
                        " "
                    );

                if (
                    index ===
                    0
                ) {
                    text =
                        text.replace(
                            /^[,.;:!?…)\]}"']+\s*/,
                            ""
                        );
                }

                paragraph.textContent =
                    text;

                body.appendChild(
                    paragraph
                );
            }
        );

        const renderedParagraphs =
            Array.from(
                body.querySelectorAll(
                    "p"
                )
            );

        const firstParagraph =
            renderedParagraphs[0];

        const lastParagraph =
            renderedParagraphs[
            renderedParagraphs.length -
            1
                ];

        /*
         * Only excerpts after the opening receive a leading
         * ellipsis.
         */
        if (
            excerptWindow.start >
            0 &&
            firstParagraph
        ) {
            firstParagraph.textContent =
                "… " +
                firstParagraph.textContent;
        }

        if (
            lastParagraph
        ) {
            const cleanEnding =
                lastParagraph.textContent
                    .trim()
                    .replace(
                        /[.!?,;:…]+$/,
                        ""
                    );

            lastParagraph.textContent =
                cleanEnding +
                "…";

            appendContinueReading(
                lastParagraph,
                getReadUrl(
                    item
                )
            );
        }

        updateExcerptControls(
            item,
            excerptIndex
        );
    }


    // ==========================================================
    // PREPARE ONE ITEM
    // ==========================================================

    function prepareItem(
        item
    ) {
        const tags =
            getTags(
                item
            );

        const bookTag =
            getBookTag(
                tags
            );

        const catalogBook =
            findCatalogBook(
                bookTag
            );

        const bookElement =
            item.querySelector(
                "[data-home-minimal-featured-book]"
            );

        const typeElement =
            item.querySelector(
                "[data-home-minimal-featured-type]"
            );

        const readMore =
            item.querySelector(
                "[data-home-minimal-featured-read-more]"
            );

        const bookName =
            catalogBook?.title ||
            getBookDisplayName(
                bookTag
            );

        const type =
            normalizeType(
                catalogBook?.type
            ) ||
            getTypeFromTags(
                tags
            );

        if (
            bookElement
        ) {
            bookElement.textContent =
                bookName
                    ? `BOOK: ${bookName}`
                    : "";
        }

        if (
            typeElement
        ) {
            typeElement.textContent =
                type ||
                "";

            typeElement.hidden =
                !type;
        }

        if (
            readMore
        ) {
            const readUrl =
                getReadUrl(
                    item
                );

            if (
                readUrl
            ) {
                readMore.href =
                    readUrl;
            }
        }

        updatePageCount(
            item
        );

        buildExcerptControls(
            item
        );

        /*
         * Always begin with excerpt 1 selected.
         */
        showExcerpt(
            item,
            0
        );
    }


    // ==========================================================
    // DISPLAY
    // ==========================================================

    function showItem(
        index
    ) {
        if (
            index <
            0 ||
            index >=
            items.length
        ) {
            return;
        }

        currentIndex =
            index;

        items.forEach(
            (
                item,
                itemIndex
            ) => {
                item.hidden =
                    itemIndex !==
                    currentIndex;
            }
        );
    }


    // ==========================================================
    // RANDOM WORK
    // ==========================================================

    function showRandomWork() {
        if (
            items.length <
            2
        ) {
            return;
        }

        let nextIndex =
            currentIndex;

        while (
            nextIndex ===
            currentIndex
            ) {
            nextIndex =
                Math.floor(
                    Math.random() *
                    items.length
                );
        }

        const currentItem =
            items[
                currentIndex
                ];

        const nextItem =
            items[
                nextIndex
                ];

        /*
         * Every newly selected work begins at excerpt 1.
         */
        showExcerpt(
            nextItem,
            0
        );

        /*
         * false = immediate swap
         * true  = fade/drift transition
         */
        if (
            !USE_HOME_READER_TRANSITION
        ) {
            showItem(
                nextIndex
            );

            return;
        }

        currentItem.classList.add(
            "is-randomWork-leaving"
        );

        window.setTimeout(
            () => {
                nextItem.classList.add(
                    "is-randomWork-entering"
                );

                showItem(
                    nextIndex
                );

                /*
                 * Paint the starting state before animating to
                 * rest.
                 */
                window.requestAnimationFrame(
                    () => {
                        window.requestAnimationFrame(
                            () => {
                                nextItem.classList.remove(
                                    "is-randomWork-entering"
                                );
                            }
                        );
                    }
                );

                currentItem.classList.remove(
                    "is-randomWork-leaving"
                );
            },
            140
        );
    }


    // ==========================================================
    // CONTROLS
    // ==========================================================

    writing.addEventListener(
        "click",
        (event) => {
            const excerptButton =
                event.target.closest(
                    "[data-home-minimal-featured-excerpt-index]"
                );

            if (
                !excerptButton
            ) {
                return;
            }

            event.preventDefault();

            const item =
                items[
                    currentIndex
                    ];

            if (
                !item
            ) {
                return;
            }

            const excerptIndex =
                Number(
                    excerptButton.dataset
                        .homeMinimalFeaturedExcerptIndex
                );

            if (
                Number.isInteger(
                    excerptIndex
                )
            ) {
                showExcerpt(
                    item,
                    excerptIndex
                );
            }
        }
    );


    /*
     * RANDOM WORK lives in the right sidebar, outside the
     * writing container.
     */
    const randomWorkButton =
        document.querySelector(
            "[data-home-minimal-featured-random-work]"
        );

    if (
        randomWorkButton
    ) {
        randomWorkButton.addEventListener(
            "click",
            (event) => {
                event.preventDefault();

                showRandomWork();
            }
        );
    }


    // ==========================================================
    // INITIALIZE
    // ==========================================================

    catalog.ready.then(
        () => {
            items.forEach(
                (item) => {
                    prepareItem(
                        item
                    );
                }
            );

            showItem(
                0
            );
        }
    );
})();